// ----------------------------------------------------------------------- imports externos
import { useEffect, useMemo, useState } from "react";
import { useOutletContext, useParams, useSearchParams } from "react-router-dom";

// ----------------------------------------------------------------------- imports internos
import Modal from "../components/modales/Modal";
import { columnas } from "../components/modales/data/Columnas";
import ModoEdicion from "../components/funcionales/ModoEdicion";
import { itemDelPadron, puedeEditar } from "../components/layout/secciones";
import { useRealtime } from "../services/store";
import { useItemPedido } from "../hooks/useItemPedido";
import { actividadDe, furgonesRTO, tractoresRTO } from "../services/porteria";
import { ETIQUETA, conActividad, etiquetaDe, puntoDe } from "../shared/rto";

/* ── EL RTO SE VE EN EL PADRÓN, NO EN UNA PANTALLA APARTE ────────────────────
 *
 * Tractores y furgones tenían cada uno DOS ítems de menú: el padrón (que
 * mostraba los datos pero no abría nada) y un «RTO de …» que era el mismo
 * listado con la columna de vencimiento y que sí abría la ficha. Dos puertas al
 * mismo lado, y encima al revés de lo esperable. Quedó una sola: el padrón trae
 * ahora la columna de RTO —con su semáforo y su filtro— y la fila entera abre la
 * ficha. La lista del lunes se arma filtrando la columna por «Vencido».
 *
 * El RTO NO es un campo del padrón: sale de `rto_tractores()` / `rto_furgones()`
 * (las mismas que alimentaban aquellas pantallas), así que se pide aparte y se
 * pega por interno. `actividadDe` es el mismo agregado que enciende el tag
 * «Sin uso?», y falla en silencio: sin él la columna dice lo de siempre. */
const RTO_DE = {
  tractores: { traer: tractoresRTO, padron: "tractor" },
  furgones: { traer: furgonesRTO, padron: "furgon" },
};

/* Los listados, como pantalla propia.
 *
 * Hasta ahora estas tablas sólo se podían ver desde adentro de otra pantalla:
 * el botón "Tractores" aparecía en Satelital, en Recursos y en los dos
 * talleres, y abría un modal. Cuatro caminos al mismo lado, y ninguno desde el
 * menú — que es donde uno lo busca primero.
 *
 * Ahora cada listado tiene su dirección y entra por el rail. El modal sigue
 * existiendo para donde tenga sentido abrirlo sin cambiar de pantalla; el
 * componente es EL MISMO, con `comoPagina`. Duplicarlo habría significado que
 * dentro de seis meses la tabla del modal y la de la pantalla muestren cosas
 * distintas.
 *
 * ── Y ACÁ SE EDITA, PERO HAY QUE DECIRLO ─────────────────────────────────
 *
 * El botón "Editar" de la ficha estaba SIEMPRE, para cualquiera que abriera la
 * pantalla: `editable` venía en `true` fijo. O sea que el permiso de escritura
 * del menú —`cargar.flota`, `cargar.gente`— decidía si la baldosa se dibujaba
 * y nada más; el que llegaba igual editaba igual.
 *
 * Ahora son dos cosas, y hacen falta las dos:
 *
 *   1. EL PERMISO, que sale del MISMO catálogo que dibuja el menú
 *      (`permisoEditar` de `SECCIONES`). Escrito acá aparte, el día que se
 *      cambie el permiso de una pantalla el menú diría una cosa y la pantalla
 *      otra — y la que manda sería la que nadie mira.
 *   2. EL LÁPIZ, que es haber dicho que vas a tocar. Con 657 tractores, una
 *      ficha que se abre para mirar y trae el botón de editar al lado es una
 *      corrección de más esperando a pasar.
 *
 * NADA DE ESTO ES LA PARED. Lo que de verdad restringe es Supabase: quien
 * quiera escribir desde la consola escribe igual. Esto es para que no se edite
 * SIN QUERER, que es el caso que pasa de verdad.
 *
 * ── Y LA PARED, HASTA HACE POCO, NO ESTABA ───────────────────────────────
 *
 * Eso de arriba era una aspiración escrita en presente. `personas` tenía una
 * política `authenticated_full_access` con `true` de los dos lados: cualquiera
 * con sesión escribía el padrón entero, y `cargar.gente` era una cortina, no
 * una llave. Está cerrado (25/08/2026): escribir pide `cargar.gente` O
 * `cargar.rrhh`, y BORRAR no lo puede nadie —a una persona se la da de baja—.
 *
 * De ahí sale que `personas` declare DOS `permisoEditar`. El catálogo y la RLS
 * tienen que decir lo mismo: si el lápiz aparece y la base rebota, el que lo
 * sufre es el que ya escribió media ficha.
 */

export const PADRONES = {
  tractores: { titulo: "Tractores", coleccion: "tractores", activos: "tractoresActivos" },
  furgones: { titulo: "Furgones", coleccion: "furgones", activos: "furgonesActivos" },
  vehiculos: { titulo: "Vehículos", coleccion: "vehiculos", activos: "vehiculosActivos" },
  personas: { titulo: "Personas", coleccion: "personas" },
  /* «Talleres y servicios» desde el 20/10: el nombre del menú del Pañol. La
     clave y la colección siguen siendo `proveedores` (la ruta es
     /padron/proveedores). */
  proveedores: { titulo: "Talleres y servicios", coleccion: "proveedores" },
  /* `fichaSinLapiz`: el EDITAR de la ficha alcanza con el permiso, sin
     prender el lápiz. Ver «EN STOCK, LA FICHA NO ESPERA AL LÁPIZ» abajo. */
  stock: { titulo: "Stock", coleccion: "stock", fichaSinLapiz: true },
  asignaciones: { titulo: "Asignaciones", coleccion: "asignaciones" },
  eventos: { titulo: "Eventos", coleccion: "eventos" },
};

const Padron = () => {
  const { que } = useParams();
  const [params] = useSearchParams();
  const { permisos = null } = useOutletContext() || {};

  /* `?buscar=` abre la tabla con el filtro puesto. Lo manda el buscador del
   * hub cuando el resultado es una persona: la gente todavía no tiene ficha
   * propia, así que la salida es su fila del padrón — y sin esto el resultado
   * llevaría a las 2.476 filas y habría que buscarla otra vez a mano.
   *
   * Se lee UNA vez, al montar, igual que `?vista=` en el RTO. */
  const [buscarInicial] = useState(() => params.get("buscar") || "");
  const cfg = PADRONES[que];

  /* `?ver=` dice CUÁL de las filas filtradas es. Igna, 02/10: «que se abra en
   * el ítem exacto, no solo la pantalla». El buscador del hub manda
   * `/padron/stock?buscar=<código>&ver=<id>` (y lo mismo con proveedores): el
   * filtro deja la lista corta y `ver` abre esa fila. La tabla hace el
   * trabajo (ver `Modal`); acá sólo se le pasa el pedido.
   *
   * Que `listo()` saque `ver` de la URL no toca la búsqueda: `buscarInicial`
   * ya se leyó al montar y vive en el estado de la tabla, no en la dirección.
   * El `?buscar=` además queda puesto, así que recargar vuelve a filtrar. */
  const itemPedido = useItemPedido();

  // Los hooks van antes de cualquier salida temprana: con una dirección
  // inventada React vería una cantidad distinta de hooks y rompe.
  const listado = useRealtime(cfg?.coleccion || "eventos");
  /* Arranca apagado y no se recuerda entre visitas: recordarlo haría que
   * alguien vuelva mañana sin acordarse de que lo dejó abierto. */
  const [editando, setEditando] = useState(false);

  /* El RTO de la unidad, para tractores y furgones. Se pide una vez por padrón
   * y no bloquea la tabla: mientras no llega, la columna dice «-» y todo lo
   * demás se ve igual. `que` en las dependencias y no `RTO_DE[que]`: el objeto
   * se rearma en cada render y volvería a disparar la consulta sin fin. */
  const [rto, setRto] = useState(null);
  const [actividad, setActividad] = useState(null);
  useEffect(() => {
    const rc = RTO_DE[que];
    setRto(null);
    setActividad(null);
    if (!rc) return;
    let vivo = true;
    rc.traer().then(
      (d) => vivo && setRto(d),
      () => vivo && setRto([]), // sin RTO la columna queda en «Sin consultar», no rota
    );
    Promise.resolve(actividadDe(rc.padron)).then(
      (a) => vivo && setActividad(a),
      () => {},
    );
    return () => {
      vivo = false;
    };
  }, [que]);

  /* Las filas del padrón con el RTO pegado por interno (`row.id` es el interno,
   * y `rto_*` viene con `interno`). Sin match —una unidad nueva que el CENT
   * todavía no tiene— queda «Sin consultar», que es lo que es. `conActividad`
   * es la que enciende «Sin uso?» cruzando con lo último que se la vio mover. */
  const datos = useMemo(() => {
    if (!RTO_DE[que] || !rto) return listado;
    const filas = conActividad(rto, actividad);
    const porInterno = new Map();
    for (const f of filas) if (f?.interno != null) porInterno.set(String(f.interno), f);
    return (listado || []).map((row) => {
      const r = porInterno.get(String(row.id));
      return r
        ? { ...row, rtoTexto: etiquetaDe(r), rtoTono: puntoDe(r) }
        : { ...row, rtoTexto: ETIQUETA.sin_consultar, rtoTono: "sin_consultar" };
    });
  }, [que, listado, rto, actividad]);

  if (!cfg) {
    return (
      <div className="panel-aviso">
        <h3>Ese listado no existe</h3>
        <p>Elegí uno del menú de la izquierda.</p>
      </div>
    );
  }

  const headers = columnas[cfg.coleccion] || [];
  const puede = puedeEditar(itemDelPadron(que), permisos);
  const tocando = puede && editando;
  /* ── EN STOCK, LA FICHA NO ESPERA AL LÁPIZ (Igna, 23/10) ──────────────────
   *
   * «Que los productos que se cargan a mano y con código de barras se puedan
   * editar desde el hub». Se podía, en teoría, y en la práctica no: el EDITAR
   * de la ficha colgaba del lápiz, y con el lápiz prendido cada celda de la
   * fila es un campo de texto que se come el clic — o sea que la ficha no se
   * abría. Lápiz apagado: ficha sin EDITAR. Lápiz prendido: no hay ficha. Y
   * el buscador del hub (`?ver=`) también la abre sin EDITAR.
   *
   * El argumento del lápiz sigue en pie para las CELDAS: convierten la tabla
   * entera en campos. Pero el EDITAR de la ficha ya es un paso deliberado —un
   * botón, un formulario, «Nada cambia hasta apretar Guardar»—, que es la
   * misma razón por la que el alta no espera al lápiz. En Stock, además,
   * completar fichas es el trabajo: los artículos del escaneo nacen con el
   * nombre a medias y sin marca.
   *
   * EL PERMISO NO SE AFLOJA: `puede` sale del mismo `permisoEditar` del menú
   * (`cargar.panol`), que es lo que pide la RLS de `stock` para escribir. */
  const fichaEditable = cfg.fichaSinLapiz ? puede : tocando;

  return (
    <>
      <div className="padron-barra">
        <ModoEdicion
          activo={editando}
          onCambiar={setEditando}
          puede={puede}
          que={`el padrón de ${cfg.titulo.toLowerCase()}`}
        />
      </div>
      <Modal
        comoPagina
        title={cfg.titulo}
        coleccion={cfg.coleccion}
        data={datos}
        headers={headers}
        buscarInicial={buscarInicial}
        itemPedido={itemPedido}
        editable={fichaEditable}
        celdasEditables={tocando}
        /* El alta NO espera al lápiz: alcanza con tener el permiso. Ver el
           porqué en `Modal`, al lado de `puedeAgregar`. */
        puedeAgregar={puede}
        onClose={() => {}}
      />
    </>
  );
};

export default Padron;
